import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import VideoBackground from '../components/VideoBackground';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MakeReferralPage = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referType: '',
    ndisNumber: '',
    planStartDate: '',
    planEndDate: '',
    financeManagement: '',
    services: [],
    interests: '',
  });
  const [loading, setLoading] = useState(false);

  const serviceOptions = [
    'Counselling Services',
    'Positive Behaviour Support',
    'Capacity Building Supports',
    'Assistance with Daily Life',
    'School Leaver Employment Support program',
    'Mental Health Support',
    'Assist Travel / Transport',
    'Household Tasks',
    'Assist Personal Activities',
    'Music Therapy',
    'Personal Training',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const honeypot = document.getElementById('referral_website')?.value || '';
      const res = await fetch(`${API_URL}/api/send-referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, honeypot }),
      });

      if (!res.ok) throw new Error('Failed to send');

      toast.success('Referral submitted successfully! We will be in touch shortly.');
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        referType: '',
        ndisNumber: '',
        planStartDate: '',
        planEndDate: '',
        financeManagement: '',
        services: [],
      });
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#81b956] focus:border-transparent transition-all text-base';
  const labelClass = 'block text-base font-semibold mb-2';

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Make a Referral" description="Refer yourself, a family member or friend for NDIS support services with Life Care and Wellness. Complete our simple referral form to get started." />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ color: '#587740' }}
            data-testid="referral-page-title"
          >
            Make a Referral
          </h1>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto mb-4"
            style={{ color: '#5e5e5e' }}
          >
            Whether it's for yourself, a family member, or someone you care about — filling out this form is the first step towards getting the genuine, compassionate support they deserve. We'll be in touch within 24 hours.
          </p>
          <p
            className="text-base max-w-2xl mx-auto font-semibold"
            style={{ color: '#587740' }}
          >
            Every referral includes a free consultation and a personalised action plan — no cost, no obligation.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <h2
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{ color: '#587740' }}
            >
              Tell Us About the Person Who Needs Support
            </h2>
            <p className="text-base mb-8" style={{ color: '#5e5e5e' }}>
              Don't worry if you don't have all the details — fill in what you can and we'll work through the rest together.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="referral-form">
              {/* Name */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Name <span className="text-red-500">*</span>
                  <span className="text-sm font-normal ml-1" style={{ color: '#999' }}>(required)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      data-testid="referral-first-name"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      data-testid="referral-last-name"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Email <span className="text-red-500">*</span>
                  <span className="text-sm font-normal ml-1" style={{ color: '#999' }}>(required)</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  data-testid="referral-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Phone <span className="text-red-500">*</span>
                  <span className="text-sm font-normal ml-1" style={{ color: '#999' }}>(required)</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="04XX XXX XXX"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  data-testid="referral-phone"
                />
              </div>

              {/* I would like to refer */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  I would like to refer
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  {['Family member', 'Friend', 'Myself'].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 px-5 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                        form.referType === option
                          ? 'border-[#81b956] bg-[#81b956]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`referral-type-${option.toLowerCase().replace(' ', '-')}`}
                    >
                      <input
                        type="radio"
                        name="referType"
                        value={option}
                        checked={form.referType === option}
                        onChange={handleChange}
                        className="w-4 h-4 accent-[#81b956]"
                      />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* NDIS Number */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  NDIS Number
                </label>
                <input
                  type="text"
                  name="ndisNumber"
                  placeholder="Enter NDIS number"
                  value={form.ndisNumber}
                  onChange={handleChange}
                  className={inputClass}
                  data-testid="referral-ndis-number"
                />
              </div>

              {/* NDIS Plan Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={{ color: '#587740' }}>
                    NDIS Plan Start Date
                  </label>
                  <input
                    type="date"
                    name="planStartDate"
                    value={form.planStartDate}
                    onChange={handleChange}
                    className={inputClass}
                    data-testid="referral-plan-start"
                  />
                </div>
                <div>
                  <label className={labelClass} style={{ color: '#587740' }}>
                    NDIS Plan End Date
                  </label>
                  <input
                    type="date"
                    name="planEndDate"
                    value={form.planEndDate}
                    onChange={handleChange}
                    className={inputClass}
                    data-testid="referral-plan-end"
                  />
                </div>
              </div>

              {/* Finances managed by */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Finances managed by
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  {['Self Managed', 'Plan Managed', 'NDIS Managed'].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 px-5 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                        form.financeManagement === option
                          ? 'border-[#81b956] bg-[#81b956]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`referral-finance-${option.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <input
                        type="radio"
                        name="financeManagement"
                        value={option}
                        checked={form.financeManagement === option}
                        onChange={handleChange}
                        className="w-4 h-4 accent-[#81b956]"
                      />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Please select the services that you are interested in
                  <span className="text-sm font-normal block mt-1" style={{ color: '#999' }}>
                    (you can choose more than one service)
                  </span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceOptions.map((service) => (
                    <label
                      key={service}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                        form.services.includes(service)
                          ? 'border-[#81b956] bg-[#81b956]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`referral-service-${service.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.services.includes(service)}
                        onChange={() => handleServiceToggle(service)}
                        className="w-4 h-4 accent-[#81b956] rounded"
                      />
                      <span className="text-sm" style={{ color: '#5e5e5e' }}>{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Individual Interests */}
              <div>
                <label className={labelClass} style={{ color: '#587740' }}>
                  Individual Interests & Passions
                  <span className="text-sm font-normal block mt-1" style={{ color: '#999' }}>
                    Tell us about the participant's hobbies, passions, and activities they enjoy — we want to know them as a person, not just a participant
                  </span>
                </label>
                <textarea
                  name="interests"
                  placeholder="e.g. music, art, gardening, cooking, sports, reading, spending time outdoors, animals..."
                  value={form.interests}
                  onChange={handleChange}
                  rows={3}
                  className={inputClass + ' resize-none'}
                  data-testid="referral-interests"
                />
              </div>

              {/* Submit */}
              {/* Honeypot field — hidden from real users */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                <label htmlFor="referral_website">Website</label>
                <input type="text" id="referral_website" name="website" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full sm:w-auto text-white text-base px-12 py-6 hover:opacity-90 transition-opacity disabled:opacity-60"
                  style={{ backgroundColor: '#81b956' }}
                  data-testid="referral-submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>

            {/* Footer links */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap gap-4 text-sm" style={{ color: '#999' }}>
              <a href="/contact" className="hover:underline">Report Abuse</a>
              <span>|</span>
              <a href="/contact" className="hover:underline">Terms of Service</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <VideoBackground variant="dark" className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Need Help With Your Referral?
          </h2>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Our friendly team is here to assist you. Give us a call and we'll
            guide you through the process.
          </p>
          <a href="tel:+611300404229">
            <Button
              size="lg"
              className="text-base px-8 py-6"
              style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
              data-testid="referral-cta-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Us Now
            </Button>
          </a>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default MakeReferralPage;
